import React from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import Icon from 'components/design-system/Icon'

const glow = (blur) => ({ filter: `drop-shadow(0 0 ${blur} var(--color-effect-glow))` })

const body14 = { fontSize: 'var(--font-size-14)', fontWeight: 'var(--font-weight-regular)' } // Body/14 Regular
const caption12Semibold = { fontSize: 'var(--font-size-12)', fontWeight: 'var(--font-weight-semibold)' } // Caption/12 Semi Bold

const regularBox = { height: 33, gap: 'var(--space-10)', px: 'var(--space-16)', py: 'var(--space-8)' }

const raisedDisabled = {
  bgcolor: 'var(--color-bg-raised)',
  borderColor: 'var(--color-border-subtle)',
  color: 'var(--color-text-disabled)'
}

/** Style theo variant Figma: base / hover / disabled. */
const VARIANTS = {
  primary: {
    base: {
      ...regularBox,
      ...body14,
      backgroundImage: 'linear-gradient(to left, var(--color-cyan-600), var(--color-blue-500))', // Gradient/Primary
      borderColor: 'var(--color-border-hover)',
      color: 'var(--color-text-primary)'
    },
    hover: {
      backgroundImage: 'linear-gradient(to left, var(--color-cyan-400), var(--color-blue-400))', // Gradient/Primary Hover
      borderColor: 'var(--color-border-focus)',
      ...glow('3.5px')
    },
    disabled: { ...raisedDisabled, backgroundImage: 'none' }
  },
  secondary: {
    base: {
      ...regularBox,
      ...body14,
      bgcolor: 'var(--color-bg-control)',
      borderColor: 'var(--color-border-default)',
      color: 'var(--color-text-secondary)'
    },
    hover: { borderColor: 'var(--color-border-hover)', color: 'var(--color-text-primary)', ...glow('2px') },
    disabled: raisedDisabled
  },
  accent: {
    base: {
      ...caption12Semibold,
      height: 32,
      gap: 'var(--space-10)',
      px: 'var(--space-16)',
      py: 'var(--space-4)',
      backgroundImage: 'linear-gradient(to right, var(--color-cyan-900), var(--color-cyan-950))', // Gradient/Accent
      borderColor: 'var(--color-border-accent)',
      color: 'var(--color-text-accent)'
    },
    hover: {
      backgroundImage: 'linear-gradient(to right, var(--color-cyan-750), var(--color-cyan-950))', // Gradient/Accent Hover
      borderColor: 'var(--color-border-hover)',
      ...glow('2px')
    },
    disabled: { backgroundImage: 'none', borderColor: 'transparent', color: 'var(--color-text-secondary)' } // "Nonactive"
  },
  ghost: {
    base: {
      ...body14,
      height: 32,
      gap: 'var(--space-8)',
      px: 'var(--space-8)',
      py: 'var(--space-4)',
      borderColor: 'transparent',
      color: 'var(--color-text-secondary)'
    },
    hover: { color: 'var(--color-text-primary)' },
    disabled: { color: 'var(--color-text-disabled)' }
  }
}

/**
 * Button (Figma 8:1741). `variant`: primary | secondary | accent | ghost. State Hover / Disabled theo :hover / `disabled`.
 * `icon` = tên icon trong registry `assets/icons` (24px, cùng màu chữ), hiện trước label.
 */
const Button = React.forwardRef(
  ({ variant = 'primary', icon, children, disabled = false, type = 'button', sx, ...props }, ref) => {
    const v = VARIANTS[variant]

    return (
      <Box
        ref={ref}
        component="button"
        type={type}
        disabled={disabled}
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
            m: 0,
            border: 'var(--stroke-default) solid transparent',
            borderRadius: 'var(--radius-md)',
            background: 'none',
            fontFamily: 'var(--font-family-primary)',
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            textDecoration: 'none',
            ...v.base,
            '&:hover:not(:disabled)': v.hover,
            '&:focus-visible': { outline: '2px solid var(--color-border-focus)', outlineOffset: '2px' },
            '&:disabled': { cursor: 'not-allowed', ...v.disabled }
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
        {...props}
      >
        {icon && <Icon name={icon} />}
        {children}
      </Box>
    )
  }
)

Button.displayName = 'Button'

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'accent', 'ghost']),
  icon: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default Button
